import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from "@sentry/angular";
import { GlobalAnimationMode, setGlobalAnimation } from '@syncfusion/ej2-base';
import { Dialog } from '@syncfusion/ej2-popups';
import { appConfig } from './app/app.config';
import { App } from './app/app';

// Syncfusion runs every animation through requestAnimationFrame, which
// browsers suspend for occluded/background tabs and delay under load. Any
// show/hide issued then never completes its lifecycle — dialogs wedge half
// open, dropdown option clicks get swallowed mid-open (the e2e "silently
// dropped click" flakiness). 'Disable' makes all Syncfusion open/close paths
// fully synchronous, at the cost of their fade/zoom transitions.
setGlobalAnimation(GlobalAnimationMode.Disable);

// Companion to the Disable above: a synchronous teardown means a DropDownList
// panel destroys itself (strips `e-popup`, detaches) inside the selection
// click, before the click bubbles to document — so the grid filter menu's
// outside-click guard (ej2-grids Filter.clickHandler) no longer recognizes the
// click as "inside a popup" and closes the whole filter dialog on operator or
// value selection. The guard whitelists `e-popup-wrapper` (DatePicker panels,
// likewise destroyed on selection), and that class is CSS-inert for dropdowns
// (every stylesheet rule mentioning it is scoped to date/datetime pickers), so
// tagging the clicked panel with it in the capture phase — before the teardown
// runs — keeps the filter dialog open. The list `<ul>` is tagged as well: a
// filtering-enabled dropdown re-parents the list out of the panel during
// teardown, so by the time the click reaches document the panel is no longer
// in the target's ancestor chain — only the list is. Re-verify these
// assumptions on Syncfusion upgrades (docs/cross-cutting/tech-debt-open-topics.md).
document.addEventListener(
  'click',
  (event) => {
    const target = event.target as Element | null;
    const panel = target?.closest?.(
      '.e-ddl.e-popup, .e-multi-select-list-wrapper.e-popup',
    );
    if (panel) {
      panel.classList.add('e-popup-wrapper');
      target?.closest('.e-list-parent')?.classList.add('e-popup-wrapper');
    }
  },
  { capture: true },
);

// Second companion: with synchronous popups, Dialog.hide() fires the dialog's
// `close` event before Syncfusion clears its own re-entry gate, so a (close)
// handler that calls hide() recurses close→hide→close until the stack
// overflows (BUG 23772). The dialog contract is therefore: bind (close) to a
// side-effects-only handler and never call hide() from it (see
// docs/cross-cutting/grid-filter-upload-dialog-scheduler-search-plan.md). This
// guard is the backstop for future regressions — a hide() re-entered during
// the synchronous close window degrades to a no-op instead of a crash. The
// flag is only held for the synchronous call frame, so animated/async closes
// and later legitimate hide() calls are unaffected.
type GuardedDialog = Dialog & { __o3HideInProgress?: boolean };
const originalDialogHide = Dialog.prototype.hide;
Dialog.prototype.hide = function (this: GuardedDialog, event?: Event): void {
  if (this.__o3HideInProgress) {
    return;
  }
  this.__o3HideInProgress = true;
  try {
    originalDialogHide.call(this, event);
  } finally {
    this.__o3HideInProgress = false;
  }
};

Sentry.init({
  dsn: "https://a98b45c7489c09809c1eb080b18d2a5a@o4509689372016645.ingest.de.sentry.io/4510160107929680",
  sendDefaultPii: true,
  integrations: [
    // send console.log, console.warn, and console.error calls as logs to Sentry
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  // Enable logs to be sent to Sentry
  enableLogs: true,
});


bootstrapApplication(App, appConfig).catch((err) => console.error(err));
